import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkle, X, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_STORAGE_KEY = 'cyboard_welcome_back_dismissed';
const NUDGE_WINDOW_HOURS = 24;

/**
 * Shows a friendly "X tasks away from your first XP burst" banner to users
 * who signed up within the last 24 hours and still have incomplete activation
 * tasks. Tapping the CTA scrolls smoothly to the ActivationChecklist already
 * rendered above on the Discover feed. Dismissal is per-session via localStorage.
 */
export function WelcomeBackNudge() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_STORAGE_KEY)) {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboarding/activation-tasks`, { withCredentials: true });
        // API returns { tasks, total, completed, progress_pct }
        if (!cancelled) setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
      } catch {
        if (!cancelled) setTasks([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Gate: user signed up within the nudge window
  const signedUpRecently = (() => {
    if (!user?.created_at) return false;
    try {
      const ts = new Date(user.created_at).getTime();
      const ageHrs = (Date.now() - ts) / (1000 * 60 * 60);
      return ageHrs >= 0 && ageHrs <= NUDGE_WINDOW_HOURS;
    } catch { return false; }
  })();

  const remaining = (tasks || []).filter((t) => !t.completed).length;
  const total = (tasks || []).length;
  const completed = total - remaining;
  const visible = !dismissed && signedUpRecently && total > 0 && remaining > 0;

  const handleDismiss = (e) => {
    e?.stopPropagation();
    if (typeof window !== 'undefined') window.localStorage.setItem(DISMISS_STORAGE_KEY, '1');
    setDismissed(true);
  };

  const handleJump = () => {
    const el = document.querySelector('[data-testid="activation-checklist"]')
      || document.querySelector('[data-checklist-anchor]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={handleJump}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="w-full text-left bg-gradient-to-r from-[#9D4CDD]/15 to-[#00F0FF]/10 border border-[#9D4CDD]/30 rounded-xl p-4 mb-3 flex items-center gap-3 group hover:border-[#9D4CDD]/60 transition-colors"
          data-testid="welcome-back-nudge"
        >
          <div className="w-10 h-10 rounded-full bg-[#9D4CDD]/20 flex items-center justify-center shrink-0">
            <Sparkle size={20} weight="fill" className="text-[#9D4CDD]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">
              Welcome back, {user?.name?.split(' ')[0] || 'player'}!
            </p>
            <p className="text-[#a0a0b0] text-xs mt-0.5">
              {remaining} {remaining === 1 ? 'task' : 'tasks'} away from your first XP burst
              {completed > 0 ? ` · ${completed}/${total} done` : ''}
            </p>
          </div>
          <ArrowRight size={16} className="text-[#9D4CDD] shrink-0 transition-transform group-hover:translate-x-0.5" />
          <span
            role="button"
            tabIndex={0}
            onClick={handleDismiss}
            onKeyDown={(e) => { if (e.key === 'Enter') handleDismiss(e); }}
            className="ml-1 p-1 -mr-1 rounded-md text-[#555] hover:text-[#999] hover:bg-white/[0.05] transition-colors shrink-0"
            data-testid="welcome-back-nudge-dismiss"
            aria-label="Dismiss welcome banner"
          >
            <X size={14} />
          </span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
